import { PricingOptions, PricingType } from "@design-system";
import { camelCase, pascalCase } from "change-case";
import { PriceOptionsUpdateWithoutPhaseInput } from "../graphql/__generated_types__/globalTypes";

export function setSingleOrRangeProps(
  pricingType: PricingType,
  field: "single" | "lower" | "upper",
  value: number | null,
  suggestedOrChosen: "suggested" | "chosen"
): PriceOptionsUpdateWithoutPhaseInput {
  const cased = {
    pricingType: camelCase(pricingType),
    field: pascalCase(field),
    estimateOrChosen: suggestedOrChosen ? pascalCase(suggestedOrChosen) : "",
  };

  const lowerUpperSingle = cased.field == "Single" ? "" : cased.field;

  return pricingType !== PricingType.EstimatedRange
    ? {
        [`${cased.pricingType}${cased.estimateOrChosen}${lowerUpperSingle}`]: {
          set: value,
        },
      }
    : {
        [`${cased.pricingType}${cased.estimateOrChosen}${lowerUpperSingle}`]: {
          set: value,
        },
      };
}

type PartialPricingOptions = Partial<PricingOptions>;

export function priceOptionsToPrismaUpdate(priceOptions: PartialPricingOptions) {
  const transformed = {
    // Capped
    ...(priceOptions.cappedChosen != null && {
      cappedChosen: { set: priceOptions.cappedChosen === 0 ? null : priceOptions.cappedChosen },
    }),
    ...(priceOptions.cappedGpm != null && {
      cappedGpm: { set: priceOptions.cappedGpm === 0 ? null : priceOptions.cappedGpm },
    }),
    ...(priceOptions.cappedSuggested != null && {
      cappedSuggested: {
        set: priceOptions.cappedSuggested === 0 ? null : priceOptions.cappedSuggested,
      },
    }),
    // Estimated
    ...(priceOptions.estimatedChosen !== undefined && {
      estimatedChosen: {
        set: priceOptions.estimatedChosen,
      },
    }),
    ...(priceOptions.estimatedGpm != null && {
      estimatedGpm: { set: priceOptions.estimatedGpm === 0 ? null : priceOptions.estimatedGpm },
    }),
    ...(priceOptions.estimatedSuggested != null && {
      estimatedSuggested: {
        set: priceOptions.estimatedSuggested === 0 ? null : priceOptions.estimatedSuggested,
      },
    }),
    //Fixed
    ...(priceOptions.fixedChosen != null && {
      fixedChosen: { set: priceOptions.fixedChosen === 0 ? null : priceOptions.fixedChosen },
    }),
    ...(priceOptions.fixedGpm != null && {
      fixedGpm: { set: priceOptions.fixedGpm === 0 ? null : priceOptions.fixedGpm },
    }),
    ...(priceOptions.fixedSuggested != null && {
      fixedSuggested: {
        set: priceOptions.fixedSuggested === 0 ? null : priceOptions.fixedSuggested,
      },
    }),
    //EstimatedRange
    ...(priceOptions.estimatedRangeGpm != null && {
      estimatedRangeGpm: {
        set: priceOptions.estimatedRangeGpm === 0 ? null : priceOptions.estimatedRangeGpm,
      },
    }),
    ...(priceOptions.estimatedRangeSuggestedLower != null && {
      estimatedRangeSuggestedLower: {
        set:
          priceOptions.estimatedRangeSuggestedLower === 0
            ? null
            : priceOptions.estimatedRangeSuggestedLower,
      },
    }),
    ...(priceOptions.estimatedRangeSuggestedUpper != null && {
      estimatedRangeSuggestedUpper: {
        set:
          priceOptions.estimatedRangeSuggestedUpper === 0
            ? null
            : priceOptions.estimatedRangeSuggestedUpper,
      },
    }),
    ...(priceOptions.estimatedRangeChosenLower !== undefined && {
      estimatedRangeChosenLower: {
        set: priceOptions.estimatedRangeChosenLower,
      },
    }),
    ...(priceOptions.estimatedRangeChosenUpper !== undefined && {
      estimatedRangeChosenUpper: {
        set: priceOptions.estimatedRangeChosenUpper,
      },
    }),
  };

  return transformed;
}
